import React, { useState, useEffect } from 'react';
import { userService } from '../../../services/userService';
import { bidangService } from '../../../services/bidangService';   // ✅ service bidang
import { useAuth } from '../../Admin/context/AuthContext';
import {
    FaPlus, FaEdit, FaTrash, FaSearch, FaUser, FaUsers,
    FaEye, FaEyeSlash, FaTimes, FaSave, FaCheckCircle,
    FaExclamationTriangle, FaChevronLeft, FaChevronRight,
    FaEnvelope, FaIdCard, FaShieldAlt, FaToggleOn, FaToggleOff, FaLock,
    FaBuilding, FaPlusCircle   // ✅ ikon untuk bidang
} from 'react-icons/fa';

const ManajemenUser = () => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [bidangs, setBidangs] = useState([]);                // ✅ state bidang
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showForm, setShowForm] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // ✅ State untuk tambah bidang baru (inline)
    const [showBidangInput, setShowBidangInput] = useState(false);
    const [newBidangName, setNewBidangName] = useState('');
    const [creatingBidang, setCreatingBidang] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        username: '',
        avatar: '',
        is_active: true,
        roles: [],
        bidang_id: '',          // ✅ field bidang
    });

    const [errors, setErrors] = useState({});

    // ✅ Ambil user dari AuthContext
    const { user } = useAuth();

    const hasPermission = (permission) => {
        if (!user) return false;
        if (user?.roles?.includes('admin')) return true;
        if (!user?.permissions) return false;
        return user.permissions.includes(permission);
    };

    // ✅ Load bidangs
    const loadBidangs = async () => {
        const result = await bidangService.getAll();
        if (result.success) {
            setBidangs(result.data || []);
        }
    };

    // Load users
    const loadUsers = async (page = 1) => {
        setLoading(true);
        const result = await userService.getUsers(page, searchTerm);
        if (result.success) {
            setUsers(result.data.data || []);
            setTotalPages(result.data.last_page || 1);
            setCurrentPage(result.data.current_page || 1);
        } else {
            showNotification(result.message, 'error');
        }
        setLoading(false);
    };

    // Load roles
    const loadRoles = async () => {
        const result = await userService.getRoles();
        if (result.success) {
            setRoles(result.data || []);
        }
    };

    useEffect(() => {
        loadUsers(1);
        loadRoles();
        loadBidangs();   // ✅ load bidang saat mount
    }, []);

    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        loadUsers(1);
    };

    const handleCreate = () => {
        setEditingUser(null);
        setFormData({
            name: '',
            email: '',
            password: '',
            username: '',
            avatar: '',
            is_active: true,
            roles: [],
            bidang_id: '',
        });
        setErrors({});
        setShowBidangInput(false);
        setNewBidangName('');
        setShowForm(true);
    };

    const handleEdit = (userItem) => {
        setEditingUser(userItem);
        setFormData({
            name: userItem.name || '',
            email: userItem.email || '',
            password: '',
            username: userItem.username || '',
            avatar: userItem.avatar || '',
            is_active: userItem.is_active === true || userItem.is_active === 1,
            roles: userItem.roles ? userItem.roles.map(r => r.id) : [],
            bidang_id: userItem.bidang_id || userItem.bidang?.id || '',   // ✅
        });
        setErrors({});
        setShowBidangInput(false);
        setNewBidangName('');
        setShowForm(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleRoleChange = (roleId) => {
        setFormData(prev => {
            const roles = prev.roles.includes(roleId)
                ? prev.roles.filter(id => id !== roleId)
                : [...prev.roles, roleId];
            return { ...prev, roles };
        });
    };

    // ✅ Buat bidang baru (inline)
    const handleCreateBidang = async () => {
        if (!newBidangName.trim()) {
            showNotification('Nama bidang tidak boleh kosong', 'error');
            return;
        }
        setCreatingBidang(true);
        const result = await bidangService.create({ nama: newBidangName.trim() });
        if (result.success) {
            showNotification('Bidang berhasil ditambahkan', 'success');
            await loadBidangs();   // refresh list
            // Auto-select bidang baru
            const newId = result.data?.id || result.data?.data?.id;
            if (newId) {
                setFormData(prev => ({ ...prev, bidang_id: newId }));
            }
            setNewBidangName('');
            setShowBidangInput(false);
        } else {
            showNotification(result.message || 'Gagal menambahkan bidang', 'error');
        }
        setCreatingBidang(false);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Nama wajib diisi';
        if (!formData.email.trim()) {
            newErrors.email = 'Email wajib diisi';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Format email tidak valid';
        }
        if (!editingUser && !formData.password) {
            newErrors.password = 'Password wajib diisi';
        }
        if (formData.password && formData.password.length < 6) {
            newErrors.password = 'Password minimal 6 karakter';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setSubmitting(true);
        const submitData = { ...formData };
        if (!submitData.password) {
            delete submitData.password;
        }
        // ✅ Pastikan bidang_id dikirim sebagai null jika kosong
        if (!submitData.bidang_id) {
            submitData.bidang_id = null;
        }

        let result;
        if (editingUser) {
            result = await userService.updateUser(editingUser.id, submitData);
        } else {
            result = await userService.createUser(submitData);
        }

        if (result.success) {
            showNotification(result.message || 'User berhasil disimpan', 'success');
            setShowForm(false);
            loadUsers(currentPage);
        } else {
            showNotification(result.message || 'Gagal menyimpan user', 'error');
        }
        setSubmitting(false);
    };

    const handleDelete = async (userItem) => {
        setDeleteConfirm(userItem);
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        const result = await userService.deleteUser(deleteConfirm.id);
        if (result.success) {
            showNotification('User berhasil dihapus', 'success');
            loadUsers(currentPage);
        } else {
            showNotification(result.message, 'error');
        }
        setDeleteConfirm(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen User</h2>
                    <p className="text-gray-600">Kelola pengguna sistem dan hak akses</p>
                </div>
                {hasPermission('users.create') && (
                    <button
                        onClick={handleCreate}
                        className="btn-primary flex items-center justify-center whitespace-nowrap"
                    >
                        <FaPlus className="mr-2" />
                        Tambah User
                    </button>
                )}
            </div>

            {notification && (
                <div className={`mb-6 p-4 rounded-lg flex items-center ${notification.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success' ? (
                        <FaCheckCircle className="mr-3 flex-shrink-0" />
                    ) : (
                        <FaExclamationTriangle className="mr-3 flex-shrink-0" />
                    )}
                    {notification.message}
                </div>
            )}

            {/* Search Bar */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
                <form onSubmit={handleSearch} className="flex gap-3">
                    <div className="relative flex-1">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Cari user berdasarkan nama, email, atau username..."
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                        />
                    </div>
                    <button type="submit" className="btn-primary flex items-center">
                        <FaSearch className="mr-2" />
                        Cari
                    </button>
                </form>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr className="text-left text-sm text-gray-500">
                                <th className="p-4 font-medium">User</th>
                                <th className="p-4 font-medium">Username</th>
                                <th className="p-4 font-medium">Bidang</th>   {/* ✅ kolom baru */}
                                <th className="p-4 font-medium">Roles</th>
                                <th className="p-4 font-medium">Status</th>
                                <th className="p-4 font-medium">Dibuat</th>
                                <th className="p-4 font-medium text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="p-8 text-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E7D32] mx-auto"></div>
                                    </td>
                                </tr>
                            ) : users.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="p-8 text-center text-gray-500">
                                        Tidak ada data user
                                    </td>
                                </tr>
                            ) : (
                                users.map((userItem) => (
                                    <tr key={userItem.id} className="border-b hover:bg-gray-50 transition-colors">
                                        <td className="p-4">
                                            <div className="flex items-center">
                                                <div className="w-10 h-10 bg-gradient-to-br from-[#0F4C3A] to-[#2E7D32] rounded-full flex items-center justify-center text-white font-bold mr-3 flex-shrink-0">
                                                    {userItem.name?.charAt(0) || '?'}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-700">{userItem.name}</p>
                                                    <p className="text-sm text-gray-500">{userItem.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4 text-gray-600">{userItem.username || '-'}</td>

                                        {/* ✅ Kolom bidang */}
                                        <td className="p-4">
                                            {userItem.bidang?.nama || userItem.bidang_nama ? (
                                                <span className="inline-flex items-center bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full">
                                                    <FaBuilding className="mr-1" />
                                                    {userItem.bidang?.nama || userItem.bidang_nama}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 text-sm">-</span>
                                            )}
                                        </td>

                                        <td className="p-4">
                                            <div className="flex flex-wrap gap-1">
                                                {userItem.roles && userItem.roles.length > 0 ? (
                                                    userItem.roles.map((role) => (
                                                        <span
                                                            key={role.id}
                                                            className="bg-green-50 text-green-700 text-xs px-2 py-1 rounded-full font-medium"
                                                        >
                                                            {role.name}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-gray-400 text-sm">-</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            {userItem.is_active === true || userItem.is_active === 1 ? (
                                                <span className="inline-flex items-center bg-green-50 text-green-700 text-xs px-3 py-1 rounded-full font-medium">
                                                    <FaToggleOn className="mr-1" /> Aktif
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center bg-gray-100 text-gray-500 text-xs px-3 py-1 rounded-full font-medium">
                                                    <FaToggleOff className="mr-1" /> Nonaktif
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-4 text-sm text-gray-500">
                                            {formatDate(userItem.created_at)}
                                        </td>
                                        <td className="p-4">
                                            <div className="flex justify-center space-x-2">
                                                {hasPermission('users.update') && (
                                                    <button
                                                        onClick={() => handleEdit(userItem)}
                                                        className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="Edit"
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                )}
                                                {hasPermission('users.delete') && (
                                                    <button
                                                        onClick={() => handleDelete(userItem)}
                                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Hapus"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                )}
                                                {!hasPermission('users.update') && !hasPermission('users.delete') && (
                                                    <span className="text-xs text-gray-400 italic">-</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                        <p className="text-sm text-gray-500">
                            Halaman {currentPage} dari {totalPages}
                        </p>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => loadUsers(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FaChevronLeft />
                            </button>
                            <button
                                onClick={() => loadUsers(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold text-[#0F4C3A] flex items-center">
                                    <FaUser className="mr-2 text-[#2E7D32]" />
                                    {editingUser ? 'Edit User' : 'Tambah User Baru'}
                                </h3>
                                <button
                                    onClick={() => setShowForm(false)}
                                    className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full"
                                >
                                    <FaTimes size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Nama */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nama Lengkap <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                            }`}
                                        placeholder="Masukkan nama lengkap"
                                    />
                                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            placeholder="Masukkan email"
                                        />
                                    </div>
                                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                                </div>

                                {/* Username */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Username
                                    </label>
                                    <div className="relative">
                                        <FaIdCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            name="username"
                                            value={formData.username}
                                            onChange={handleInputChange}
                                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                                            placeholder="Masukkan username (opsional)"
                                        />
                                    </div>
                                </div>

                                {/* ✅ BIDANG */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        <FaBuilding className="inline mr-1 text-[#2E7D32]" /> Bidang
                                    </label>

                                    {bidangs.length > 0 ? (
                                        // Dropdown bidang
                                        <select
                                            name="bidang_id"
                                            value={formData.bidang_id}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                                        >
                                            <option value="">-- Pilih Bidang --</option>
                                            {bidangs.map((b) => (
                                                <option key={b.id} value={b.id}>
                                                    {b.nama || b.name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        // Kalau belum ada data bidang
                                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                                            Belum ada data bidang. Silakan tambahkan bidang baru terlebih dahulu.
                                        </div>
                                    )}

                                    {/* Tombol "Tambah Bidang Baru" */}
                                    {!showBidangInput ? (
                                        <button
                                            type="button"
                                            onClick={() => setShowBidangInput(true)}
                                            className="mt-2 text-sm text-[#2E7D32] hover:text-[#4CAF50] flex items-center gap-1"
                                        >
                                            <FaPlusCircle size={12} /> Tambah Bidang Baru
                                        </button>
                                    ) : (
                                        // Inline form tambah bidang
                                        <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
                                            <label className="block text-xs font-semibold text-gray-600">
                                                Nama Bidang Baru
                                            </label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={newBidangName}
                                                    onChange={(e) => setNewBidangName(e.target.value)}
                                                    placeholder="Contoh: Sekretariat"
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#2E7D32]"
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleCreateBidang();
                                                        }
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleCreateBidang}
                                                    disabled={creatingBidang}
                                                    className="bg-[#2E7D32] hover:bg-[#4CAF50] text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1 disabled:opacity-50"
                                                >
                                                    {creatingBidang ? (
                                                        <>
                                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                                            Menyimpan...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FaSave size={12} /> Simpan
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setShowBidangInput(false);
                                                        setNewBidangName('');
                                                    }}
                                                    className="px-3 py-2 text-gray-500 hover:text-gray-700 text-sm"
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Password */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Password {!editingUser && <span className="text-red-500">*</span>}
                                        {editingUser && <span className="text-gray-400 text-xs">(kosongkan jika tidak diubah)</span>}
                                    </label>
                                    <div className="relative">
                                        <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            className={`w-full pl-10 pr-12 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent ${errors.password ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            placeholder="Masukkan password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                    {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                                </div>

                                {/* Roles */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Roles <span className="text-red-500">*</span>
                                    </label>
                                    <div className="space-y-2">
                                        {roles.map((role) => (
                                            <label key={role.id} className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.roles.includes(role.id)}
                                                    onChange={() => handleRoleChange(role.id)}
                                                    className="mr-3 text-[#2E7D32] focus:ring-[#2E7D32] w-5 h-5"
                                                />
                                                <FaShieldAlt className="text-gray-400 mr-2" />
                                                <div>
                                                    <span className="text-sm font-medium text-gray-700">{role.name}</span>
                                                    <p className="text-xs text-gray-500 capitalize">{role.guard_name}</p>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Status */}
                                <div>
                                    <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                                        <input
                                            type="checkbox"
                                            name="is_active"
                                            checked={formData.is_active}
                                            onChange={handleInputChange}
                                            className="mr-3 text-[#2E7D32] focus:ring-[#2E7D32] w-5 h-5"
                                        />
                                        <div>
                                            <span className="text-sm font-medium text-gray-700">Akun Aktif</span>
                                            <p className="text-xs text-gray-500">User dapat login jika akun aktif</p>
                                        </div>
                                    </label>
                                </div>

                                <div className="flex justify-end space-x-3 pt-4 border-t">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className={`flex items-center bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg transition-all ${submitting ? 'opacity-75 cursor-not-allowed' : ''
                                            }`}
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                Menyimpan...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="mr-2" />
                                                {editingUser ? 'Update User' : 'Simpan User'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FaTrash className="text-3xl text-red-500" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">Hapus User?</h3>
                            <p className="text-gray-600 mb-6">
                                Anda akan menghapus user <strong>{deleteConfirm.name}</strong>.
                                Tindakan ini tidak dapat dibatalkan.
                            </p>
                            <div className="flex justify-center space-x-3">
                                <button
                                    onClick={() => setDeleteConfirm(null)}
                                    className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Batal
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="px-6 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors"
                                >
                                    Hapus
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenUser;